import { wrapConversationKey } from './chatCrypto';
import type { KeyGrantUpload, MemberKeyInfo, SecurityEvent } from './chatService';

/**
 * Pure helpers behind the grant scan, the approval dialog, and the system
 * lines — kept free of React state so the rules are unit-testable on their own.
 */

export type GrantErrorCode =
  | 'caller_pending'
  | 'grant_invalid'
  | 'grantee_pending'
  | 'grant_stale'
  | 'key_version_conflict';

const GRANT_ERROR_CODES: GrantErrorCode[] = [
  'caller_pending',
  'grant_invalid',
  'grantee_pending',
  'grant_stale',
  'key_version_conflict',
];

export function grantErrorCode(error: unknown): GrantErrorCode | null {
  const code = (error as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' && (GRANT_ERROR_CODES as string[]).includes(code)
    ? (code as GrantErrorCode)
    : null;
}

/** A holder for the current epoch: a members entry whose keyVersions contains it (client-side estimate). */
export function holderForEpoch(members: MemberKeyInfo[], keyVersion: number): boolean {
  return members.some((member) => member.keyVersions.includes(keyVersion));
}

/** The held versions ≥ the readable floor that `member` lacks. */
export function versionsToGrant(
  held: number[],
  member: MemberKeyInfo,
  minimumReadableKeyVersion: number,
): number[] {
  return held
    .filter((version) => version >= minimumReadableKeyVersion && !member.keyVersions.includes(version))
    .sort((a, b) => a - b);
}

/**
 * Automatic grants (no click): every OTHER member with a published key,
 * `keyChangePending: false`, and `keyVersions` lacking a readable version the
 * granting client holds.
 */
export function membersForAutomaticGrant(
  members: MemberKeyInfo[],
  held: number[],
  minimumReadableKeyVersion: number,
  selfUid: string,
): MemberKeyInfo[] {
  return members.filter(
    (member) =>
      member.uid !== selfUid &&
      member.publicKey != null &&
      !member.keyChangePending &&
      versionsToGrant(held, member, minimumReadableKeyVersion).length > 0,
  );
}

/** Approval grants (one click each): every other member with `keyChangePending: true` and a key. */
export function membersAwaitingApproval(members: MemberKeyInfo[], selfUid: string): MemberKeyInfo[] {
  return members.filter(
    (member) => member.uid !== selfUid && member.publicKey != null && member.keyChangePending,
  );
}

/** A bootstrap set: every member with a published, non-pending key (the bootstrapper included). */
export function bootstrapGrantees(members: MemberKeyInfo[]): MemberKeyInfo[] {
  return members.filter((member) => member.publicKey != null && !member.keyChangePending);
}

/** Wrap `versions` of the conversation key for one member's current public key. */
export async function buildGrantsForMember(
  ckByVersion: Map<number, Uint8Array>,
  member: MemberKeyInfo,
  conversationId: string,
  versions: number[],
): Promise<KeyGrantUpload[]> {
  const publicKey = member.publicKey;
  if (!publicKey) return [];
  return Promise.all(
    versions.map(async (keyVersion) => ({
      granteeUid: member.uid,
      granteePublicKey: publicKey,
      wrappedCk: await wrapConversationKey(ckByVersion.get(keyVersion) as Uint8Array, publicKey, {
        conversationId,
        granteeUid: member.uid,
        keyVersion,
      }),
    })),
  );
}

/** The display name for a system line: the resolved subject, or "A former member". */
export function eventSubjectName(event: SecurityEvent): string {
  return event.subjectDisplayName || 'A former member';
}

/** The system line for an event, or null for events that produce no row (emergency reset). */
export function systemLineText(event: SecurityEvent): string | null {
  switch (event.type) {
    case 'identity_key_replaced':
      return `Encryption key for ${eventSubjectName(event)} changed`;
    case 'member_added':
      return `${eventSubjectName(event)} joined the study team`;
    case 'member_removed':
      return `${eventSubjectName(event)} left the study team`;
    default:
      return null;
  }
}
